import React, { forwardRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import Tooltip from '../tooltip/Tooltip';
import Loader from '../loader/Loader';
import Divider from '../divider/Divider';
import { trackGtm } from '../../utils';

const PAYMENT_TYPES = {
  FUNDS_PAYMENT: 'Funds payment',
  INTEREST_ONLY_REPAYMENT: 'Paid by the government',
  REPAYMENT: 'Repayment',
  MISSED_PAYMENT: 'Repayment Holiday',
  PAYMENT_HOLIDAY: 'Repayment holiday, no repayment'
};

const PAYMENT_SUBTYPES = {
  TOTAL_REPAYMENT_HOLIDAY: 'Repayment holiday, no repayment',
  REPAYMENT_HOLIDAY_INTEREST_ONLY: 'Repayment holiday, only pay interest'
};

const paymentLabel = (type, subtype) => {
  if (subtype) {
    return PAYMENT_SUBTYPES[subtype];
  }

  return PAYMENT_TYPES[type];
};

const formatAmountObject = (amountObject) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: amountObject.currency }).format(
    amountObject.minorUnits / 100
  );

const PaymentRow = ({ payments }) => {
  return (
    <tr>
      <td>{payments.paymentDate}</td>
      <td>{paymentLabel(payments.type, payments.subType)}</td>
      <td>
        {payments.type !== 'MISSED_PAYMENT' && payments.type !== 'PAYMENT_HOLIDAY' ? (
          <>
            {formatAmountObject(payments.amount)}{' '}
            {payments.type !== 'FUNDS_PAYMENT' && (
              <Tooltip
                bio={`${formatAmountObject(payments.principal)} principal + ${formatAmountObject(
                  payments.interest
                )} interest`}
              />
            )}
          </>
        ) : (
          formatAmountObject({ currency: 'GBP', minorUnits: 0 })
        )}
      </td>
    </tr>
  );
};

const ValidationMessage = ({ message }) => {
  let publicMessage;

  switch (message) {
    case 'TOO_MANY_TOTAL_REPAYMENT_HOLIDAYS':
      publicMessage = 'Too many total repayment holidays';
      break;

    default:
      publicMessage = 'There was a problem calculating the payments, please try again';
      break;
  }

  return <p className="validation-error">{publicMessage}</p>;
};

const Checkbox = forwardRef(({ onClick, disabled, ...props }, ref) => (
  <label className="checkbox--label">
    <input type="checkbox" onClick={onClick} disabled={disabled} ref={ref} {...props} />
    <span className="checkmark"></span>
  </label>
));

const LoanRepaymentCalculator = () => {
  const [showLoader, setShowLoader] = useState(false);
  const [calculationResult, setCalculationResult] = useState(null);
  const [calculationErrors, setCalculationErrors] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue
  } = useForm();

  const watchLoanAmount = watch('loanAmount');
  const watchExtendLoanTerm = watch('extendLoanTerm', 'false');

  const onSubmit = (data) => {
    let payload = { ...data };

    payload.loanAmount = {
      currency: 'GBP',
      minorUnits: data.loanAmount * 100
    };

    // Filter out rows where nothing is ticked
    payload.loanRepaymentHolidays = payload.loanRepaymentHolidays.filter((tableRow) => {
      return tableRow.standard !== false || tableRow.increaseLoanTerm !== false;
    });

    // Set defaults/format bools
    payload.loanRepaymentHolidays = payload.loanRepaymentHolidays.map((holiday) => {
      return {
        repaymentHolidayType: holiday.repaymentHolidayType,
        increaseLoanTerm: String(holiday.increaseLoanTerm)
      };
    });

    setCalculationResult(null);
    setCalculationErrors(null);
    setShowLoader(true);

    fetch(window.starlingbank.getConfig('loanRepaymentCalculator'), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then((response) => response.json())
      .then((data) => {
        setCalculationResult(data);
      })
      .catch((error) => {
        if (error.status === 500) {
          setCalculationErrors(['500']);
        } else {
          setCalculationErrors(error.data);
        }
      })
      .finally(() => {
        setShowLoader(false);
        trackGtm({ event: 'loan-repayment-calculator-submit' });
      });
  };

  const clearDependent = (dependent) => {
    setValue(dependent, false);
    setCalculationResult(null);
  };

  const changeTerm = (e) => {
    setCalculationResult(null);
    if (e.target.value === 'false') return;
    const selectedExtendTermCheckboxes = document.querySelectorAll(
      'label.checkbox--label > input[name$=".increaseLoanTerm"]:checked'
    );
    selectedExtendTermCheckboxes.forEach((checkbox) => setValue(checkbox.name, false));
  };

  return (
    <div className="loan-repayment-calculator">
      <div className="loan-repayment-calculator--columns">
        <div className="loan-repayment-calculator--columns-form">
          <form onSubmit={handleSubmit(onSubmit)}>
            <section className="loan-repayment-calculator--columns-form-top">
              <div className="loan-repayment-calculator--form-element">
                <label htmlFor="loan-amount">Loan Amount</label>
                <span className="pound">
                  <input
                    type="text"
                    id="loan-amount"
                    className="pound"
                    data-testid="loanAmount"
                    aria-invalid={errors.loanAmount ? 'true' : 'false'}
                    {...register('loanAmount', {
                      required: true,
                      min: 2000,
                      max: 50000,
                      onChange: () => setCalculationResult(null)
                    })}
                  />
                </span>
                {errors.loanAmount && (
                  <>
                    {errors.loanAmount?.type === 'required' && (
                      <span className="validation-error" role="alert">
                        This field is required
                      </span>
                    )}
                    {errors.loanAmount?.type === 'min' && (
                      <span className="validation-error" role="alert">
                        The loan amount must be greater than or equal to 2000
                      </span>
                    )}
                    {errors.loanAmount?.type === 'max' && (
                      <span className="validation-error" role="alert">
                        The loan amount must be less than or equal to 50000
                      </span>
                    )}
                  </>
                )}
              </div>

              <div className="loan-repayment-calculator--form-element">
                <label htmlFor="loan-period">Loan Period</label>
                {/* I need immediate feeback as soon as you've changed the option, if you change then click calculate straight away then it's too late */}
                {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                <select
                  id="loan-period"
                  aria-invalid={errors.extendLoanTerm ? 'true' : 'false'}
                  {...register('extendLoanTerm', { required: true, onChange: changeTerm })}
                >
                  <option value="false">6 Years</option>
                  <option value="true">10 Years</option>
                </select>
                {errors.extendLoanTerm && (
                  <span className="validation-error" role="alert">
                    This field is required
                  </span>
                )}
              </div>
            </section>

            <div>
              <h3>Payment Holidays (Optional)</h3>
              <p>
                All payment holidays are for six months: you can pay only interest (up to three times) or make no
                payments. If you’ve not extended your term to ten years already, you can choose to extend your loan by
                six months for each time you take a payment holiday.
              </p>

              <div className="form-table--headings">
                <div></div>
                <div>
                  Without changing
                  <br />
                  your loan term
                  <Tooltip
                    heading="Without changing your loan term"
                    bio="This option selects the payment holiday without changing the term of your loan"
                  />
                </div>
                <div>
                  Increase your loan term
                  <br />
                  by six months
                  <Tooltip
                    heading="Increase your loan term by six months"
                    bio="You can increase the term by six months (for each payment holiday selected). You can’t do this if you have already chosen to extend your loan to ten years"
                  />
                </div>
              </div>

              <div className="form-table">
                <div>
                  Just pay interest (1)<sup>*</sup>
                </div>
                <div>
                  <input
                    type="hidden"
                    value="INTEREST_ONLY_REPAYMENTS"
                    {...register('loanRepaymentHolidays.0.repaymentHolidayType')}
                  />
                  <Checkbox
                    onClick={() => clearDependent('loanRepaymentHolidays.0.increaseLoanTerm')}
                    {...register('loanRepaymentHolidays.0.standard')}
                  />
                </div>
                <div>
                  <Checkbox
                    onClick={() => clearDependent('loanRepaymentHolidays.0.standard')}
                    {...register('loanRepaymentHolidays.0.increaseLoanTerm')}
                    disabled={watchExtendLoanTerm === 'true'}
                  />
                </div>
                <div>
                  Just pay interest (2)<sup>*</sup>
                </div>
                <div>
                  <input
                    type="hidden"
                    value="INTEREST_ONLY_REPAYMENTS"
                    {...register('loanRepaymentHolidays.1.repaymentHolidayType')}
                  />
                  <Checkbox
                    onClick={() => clearDependent('loanRepaymentHolidays.1.increaseLoanTerm')}
                    {...register('loanRepaymentHolidays.1.standard')}
                  />
                </div>
                <div>
                  <Checkbox
                    onClick={() => clearDependent('loanRepaymentHolidays.1.standard')}
                    {...register('loanRepaymentHolidays.1.increaseLoanTerm')}
                    disabled={watchExtendLoanTerm === 'true'}
                  />
                </div>
                <div>
                  Just pay interest (3)<sup>*</sup>
                </div>
                <div>
                  <input
                    type="hidden"
                    value="INTEREST_ONLY_REPAYMENTS"
                    {...register('loanRepaymentHolidays.2.repaymentHolidayType')}
                  />
                  <Checkbox
                    onClick={() => clearDependent('loanRepaymentHolidays.2.increaseLoanTerm')}
                    {...register('loanRepaymentHolidays.2.standard')}
                  />
                </div>
                <div>
                  <Checkbox
                    onClick={() => clearDependent('loanRepaymentHolidays.2.standard')}
                    {...register('loanRepaymentHolidays.2.increaseLoanTerm')}
                    disabled={watchExtendLoanTerm === 'true'}
                  />
                </div>
                <div>
                  No payments<sup>*</sup>
                </div>
                <div>
                  <input
                    type="hidden"
                    value="TOTAL_REPAYMENT_HOLIDAY"
                    {...register('loanRepaymentHolidays.3.repaymentHolidayType')}
                  />
                  <Checkbox
                    onClick={() => clearDependent('loanRepaymentHolidays.3.increaseLoanTerm')}
                    {...register('loanRepaymentHolidays.3.standard')}
                  />
                </div>
                <div>
                  <Checkbox
                    onClick={() => clearDependent('loanRepaymentHolidays.3.standard')}
                    {...register('loanRepaymentHolidays.3.increaseLoanTerm')}
                    disabled={watchExtendLoanTerm === 'true'}
                  />
                </div>
              </div>
            </div>

            <p>
              <small>
                <sup>*</sup>For six months
              </small>
            </p>

            <input type="submit" value="Calculate" data-testid="calculatorSubmit" />
          </form>
        </div>

        <Divider />

        <div className="loan-repayment-calculator--columns-calculation">
          <h3>Your repayments</h3>
          <div className="ilc-result">
            {calculationResult ? (
              <>
                <div className="ilc-result--total-due">
                  <div>
                    <span>Total amount repayable</span>
                    <span>{formatAmountObject(calculationResult.newTotalAmountOwed)}</span>
                  </div>
                  <div>
                    <span>Loan amount</span>
                    <span>{formatAmountObject({ currency: 'GBP', minorUnits: watchLoanAmount * 100 })}</span>
                  </div>
                  <div>
                    <span>Term (months)</span>
                    <span>
                      {
                        calculationResult.newUpcomingPayments.filter((payment) => payment.type !== 'FUNDS_PAYMENT')
                          .length
                      }
                    </span>
                  </div>
                  <div>
                    <span>APR</span>
                    <span>2.5%</span>
                  </div>
                </div>
                <div className="ilc-result--breakdown">
                  <Divider customPadding="top-bottom" />
                  <table>
                    <thead>
                      <tr>
                        <th>Payment Date</th>
                        <th>Type</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calculationResult.newUpcomingPayments.map((payment) => (
                        <PaymentRow key={payment.paymentDate + '-' + payment.amount.minorUnits} payments={payment} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <>
                {!showLoader && <p>Fill out the form to calculate payments</p>}
                {calculationErrors && (
                  <>
                    {calculationErrors.map((errorMessage) => (
                      <ValidationMessage key={errorMessage} message={errorMessage} />
                    ))}
                  </>
                )}
              </>
            )}
            {showLoader && <Loader />}
          </div>
        </div>
        <p>
          Please note that this calculator is for illustrative purposes only. The calculations provided should not be
          construed as financial advice and do not constitute a legal offer.
        </p>
      </div>
    </div>
  );
};

export default LoanRepaymentCalculator;
